import { useCurrentProfile } from '@map/state/profile';
import { Image } from '@map/types';
import useUpdateEffect from '@shared/hooks/useUpdateEffect';
import { useMemo } from 'react';
import { cache } from 'swr/_internal';
import { MobileApiPath, remoteDataChanged, useRemoteData } from './baseApi';
import { useLocationParams } from './searchLocation';

export type OccupationCategory = {
  id: number;
  title: string;
  description: string;
  illustration: string;
  image?: Image;
  occupations?: string[];
  occupationCount: number;
};

export type OccupationCategoryWithStats = OccupationCategory & {
  courseCount?: number;
  careersCount?: number;
  coursesCount?: number;
  profileStats?: {
    visits: number;
    matchingSkills: number;
    recommendedOccupations: number;
    skillMatchedOccupations: number;
    careerGoals: number;
    // these only show up if optional features are enabled
    interestMatchedOccupations?: number;
    topMatchedOccupations?: number;
    jobVacancies?: number;
  };
};

// all known categories, even if they
// currently have no available career occupations
export const useOccupationCategories = () => {
  const [data = []] = useRemoteData<OccupationCategory[]>({
    url: 'occupation_categories',
  });
  return data;
};

type CareerCategoriesParams = {
  includeLocationParams?: boolean;
};
// occupation categories of currently available career occupations
export const useCareerCategories = (params?: CareerCategoriesParams) => {
  const locationParams = useLocationParams();
  const [data = [], , { isLoading: isEveryCareerFieldsLoading }] = useRemoteData<
    OccupationCategory[]
  >({
    url: 'occupation_categories?career=1',
  });
  const [dataWithStats = [], , { isLoading: isStatsLoading }] = useRemoteData<
    OccupationCategoryWithStats[]
  >({
    url: `occupation_categories?career=1&profileStats=1${
      params?.includeLocationParams ? `&${locationParams}` : ''
    }`,
  });
  const mergedData = useMemo<Array<Either<OccupationCategory, OccupationCategoryWithStats>>>(() => {
    const map = Object.fromEntries(dataWithStats.map((c) => [c.id, c]));
    return data.map((category) => map[category.id] || category);
  }, [data, dataWithStats]);
  return [mergedData, isEveryCareerFieldsLoading || isStatsLoading] as const;
};

export const useCareerCategory = (
  categoryId: number,
  { stats, includeLocationParams }: { stats?: string[]; includeLocationParams?: boolean } = {},
) => {
  const profile = useCurrentProfile();
  const locationParams = useLocationParams();
  const result = useRemoteData<OccupationCategoryWithStats>({
    url: categoryId
      ? `occupation_categories/${categoryId}?${stats ? `stats=${stats.join(',')}` : ''}${
          includeLocationParams ? `&${locationParams}` : ''
        }`
      : null,
  });
  const [, mutate] = result;
  useUpdateEffect(() => void mutate(), [profile.search_location]);
  return result;
};

// ranked listing of categories recommended (in various ways) for the
// current occupation
type CategoryRanking = 'jobVacancies' | 'interestMatchedOccupations';
export const useRecommendedCategories = (ranking: CategoryRanking) => {
  const locationParams = useLocationParams();
  const [data = [], mutate, { isLoading }] = useRemoteData<OccupationCategoryWithStats[]>({
    url: `occupation_categories/recommended?ranking=${ranking}${
      ranking === 'jobVacancies' ? `&${locationParams}` : ''
    }`,
  });
  return [data, mutate, isLoading] as const;
};

export const recommendedCategoriesChanged = () => {
  for (const key of cache.keys()) {
    if (key.includes('occupation_categories/recommended')) {
      remoteDataChanged(key as MobileApiPath);
    }
  }
};
